import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { stageRig } from '../lighting/stage-light-rig.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';

// Shared-rig contract (see the framing addendum). intensityScale 0.6: at
// full strength the beam cones flatten this scene, since the tubes are now
// close to the camera and fill the frame. zones 'floor' keeps every head
// aiming at the floor and the tube volume, never down the camera->artwork
// axis.
const RIG = 'required';
const RIG_OWNER = 'kineticrig';
const RIG_INTENSITY_SCALE = 0.6;
const RIG_ZONES = 'floor';
// A 'floor' owner also gets the rig's spot falloff relaxed (decay 2.4 -> 1.1,
// see StageLightRig.setOwnerOptions), which alone brings the light within
// ~1/10 rather than ~1/170 at floor distance. This is the remaining trim on
// top of that. Scales spot.intensity only, never the cone meshes.
// 5, not 3: intensityScale now dims spot.intensity too (it did not when this
// was first tuned), so 0.6 * 5 lands on the same 3x this visualizer's floor
// pools were calibrated against. Owners at intensityScale 1 are unaffected.
const RIG_FLOOR_LIGHT_BOOST = 5;
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'kineticrig',
  family: 'stage',
  energyLevel: 0.80,
  bpmMin: 108,
  bpmMax: 178,
  movement: 0.85,
  danceability: 0.80,
  tonality: 0.70,
  enabled: true
};

// KineticRig: a suspended matrix of vertical LED tubes on virtual winches -
// the mainstage "kinetic lights" gesture (Tomorrowland/Ultra, KineLED-style
// rigs, deadmau5' cube). The tubes rise/fall as a topographic surface in the
// air, and every beat sends a circular shockwave rolling physically through
// the matrix.
//
// Deliberately NOT an equalizer (neon-grid.js already owns that read): these
// are objects in space with real depth parallax, seen from UNDERNEATH the
// rig rather than from in front of it.
//
// FRAMING (see the addendum - this is what the first version got wrong):
//  - The camera sits under the rig, close in. The near row is ~4 units away
//    and the far row ~26, a ~6.6:1 apparent-size ratio, so the field reads
//    as deep instead of as a flat wall.
//  - Row depth is deliberately non-linear (pow 1.6), which packs rows toward
//    the front where the parallax is worth spending tubes on.
//  - Column span is computed from the camera at runtime so the middle row
//    overflows the frame by 40% on each side. Tubes ARE cut by the left,
//    right and top edges: that is what makes the rig read as bigger than
//    the screen. Never hard-code the grid width.
//  - A central arch lifts the columns near x=0, opening a vault of light
//    above the cover so the artwork is framed rather than left floating.
//
// PERFORMANCE SHAPE (fill-rate bound on VideoCore VI / Mali, not triangles):
//  - ONE draw call for all tubes (InstancedBufferGeometry, 2 triangles per
//    instance), one more for the optional reflection, one for the floor.
//  - Zero per-frame attribute uploads. All animation is vertex-shader work
//    driven by scalar uniforms; even the resize-dependent column span is a
//    uniform (uSpanX) rather than a rebuilt position buffer.
//  - mediump fragment precision, no discard, no texture, no noise.
//  - No point sprites, so the ALIASED_POINT_SIZE_RANGE clamp used elsewhere
//    in this codebase does not apply here.
//
// Each tube is one quad, cylindrically billboarded around Y only: it yaws to
// face the camera horizontally but stays rigorously vertical.

const TUBE_TOTAL_WIDTH = 0.3; // full quad width - the glowing halo
const TUBE_CORE_FRACTION = 0.1 / 0.3; // the hot white core is 0.10 units of that
const TUBE_LENGTH = 3.0;
// The winch pays cable out between these two heights; a tube at the bottom
// of its travel just grazes the floor (3.0 - TUBE_LENGTH == 0).
const ANCHOR_Y_MIN = 3.0;
const ANCHOR_Y_MAX = 8.4;
const ANCHOR_Y_BASE = 4.6;

const CAMERA_POS = [0, 3.2, 6.5];
const CAMERA_LOOK = [0, 3.4, -3.0];

// z(row) = ROW_Z_NEAR - ROW_Z_SPAN * (row/(rows-1))^ROW_Z_POWER
const ROW_Z_NEAR = 2.5;
const ROW_Z_SPAN = 22.5;
const ROW_Z_POWER = 1.6;
// The middle row overflows the visible width by this factor (1.4 == 40% off
// each side), per the addendum's framing rule.
const SPAN_X_OVERFLOW = 1.4;

const HEIGHT_AMPLITUDE = 2.4; // world units of vertical travel at full amplitude
const LENGTH_MODULATION = 0.15; // +/-15% length from the same field, phase-shifted
const ARCH_HEIGHT = 2.2; // central columns lift by this much
const ARCH_WIDTH = 3.4; // gaussian half-width of the vault, in world X
const RING_LIFT = 0.9; // upward impulse a shockwave adds
const ATMOSPHERIC_DENSITY = 0.045; // explicit exp(-dist * k) haze - FogExp2 does not reach custom ShaderMaterials

const RING_SLOTS = 4;
const RING_DECAY = 2.2;
const RING_STRENGTH_DOWNBEAT = 1.0;
const RING_STRENGTH_OFFBEAT = 0.55;
const BEATS_PER_BAR = 4;

const REFERENCE_BPM = 120;
const TEMPO_MULTIPLIER_MIN = 0.6;
const TEMPO_MULTIPLIER_MAX = 1.8;
const NO_TEMPO_RATE = 0.8;

const BEAT_PULSE_AMOUNT = 0.45;
const BEAT_PULSE_DECAY = 9;

const PALETTE_LERP_S = 0.6;
const FALLBACK_COLOR_A = 0xff2fb0;
const FALLBACK_COLOR_B = 0x2fd8ff;
const ENERGY_THRESHOLD = 0.6;

const REFLECTION_OPACITY = 0.22;
const REFLECTION_DESAT = 0.25;
// The higher a tube hangs, the weaker its reflection - without this the
// mirrored copy reads as a second, equally solid rig under the floor.
const REFLECTION_FALLOFF = 0.16;

const FLOOR_SIZE = 60;
// The addendum specifies 0x050507, but that is ~0.0002 in linear space once
// sRGB-decoded: a surface reflecting 0.02% of the light it receives cannot
// show a light pool at any intensity, so "0x050507" and "montrer les flaques
// des SpotLight" are mutually exclusive. Kept as dark as a black stage floor
// reads while still being able to catch light at all.
const FLOOR_COLOR = 0x16161c;

const TIER_LAYOUT = {
  0: { cols: 11, rows: 5, reflection: false, radial: false },
  1: { cols: 15, rows: 7, reflection: false, radial: true },
  2: { cols: 19, rows: 8, reflection: true, radial: true }
};
// Tier 1 by default: both shipping targets (Pi 4 / Tizen TV) are fill-rate
// bound and the reflection roughly doubles covered pixels. Tier 2 is an
// explicit opt-in - new KineticRigVisualizer({ qualityTier: 2 }).
const DEFAULT_TIER = 1;


const vertexShader = `
  precision highp float;

  uniform float uTime;
  uniform float uAmplitude;
  uniform float uEnergy;
  uniform float uRingSpeed;
  uniform float uSpanX;
  uniform vec4 uRings[${RING_SLOTS}];
  uniform vec3 uColorA;
  uniform vec3 uColorB;
  uniform float uPulse;
  uniform float uMirror;

  // (columnNormalised -0.5..0.5, worldZ, phase, rowNormalised 0..1)
  attribute vec4 iTube;

  varying vec3 vColor;
  varying float vGlow;
  varying float vU;
  varying float vV;
  varying float vFog;
  varying float vSafeAtt;

  ${SAFE_ZONE_VERTEX_CHUNK}

  float heightField(vec2 g, float t) {
    float h = sin(g.x * 0.28 + t * 0.9) * 0.6
            + sin(g.y * 0.6 + t * 0.7 + 1.7) * 0.4;
    #ifdef USE_RADIAL
      h += sin(length(g) * 0.45 - t * 0.5) * 0.25;
    #endif
    h += uEnergy * sin(g.x * 0.35 + g.y * 0.5 + t * 1.3) * 0.3;
    return h;
  }

  // One shockwave slot. Called RING_SLOTS times explicitly rather than in a
  // loop - guaranteed unrolled on the oldest WebGL1 drivers we still target.
  float ringHit(vec4 ring, vec2 g, float t) {
    float age = t - ring.z;
    float live = step(0.0001, ring.w) * step(0.0, age);
    float radius = age * uRingSpeed;
    float d = abs(distance(g, ring.xy) - radius);
    float hit = ring.w * exp(-age * ${RING_DECAY.toFixed(1)}) * (1.0 - smoothstep(0.0, 0.9, d));
    return hit * live;
  }

  void main() {
    float t = uTime;
    // Column span is a uniform, so a resize re-lays the grid without ever
    // touching a vertex buffer.
    vec2 g = vec2(iTube.x * uSpanX, iTube.y);

    float field = heightField(g, t + iTube.z * 0.35);
    float stretch = 1.0 + heightField(g, t + 2.3 + iTube.z * 0.35) * ${LENGTH_MODULATION.toFixed(3)};

    float rings = ringHit(uRings[0], g, t)
                + ringHit(uRings[1], g, t)
                + ringHit(uRings[2], g, t)
                + ringHit(uRings[3], g, t);

    // The vault: columns near x=0 ride higher, opening a gap of light
    // directly above the cover. Written as exp(-a*a) rather than pow(x,2.0)
    // to keep it a multiply.
    float ax = g.x / ${ARCH_WIDTH.toFixed(2)};
    float arch = ${ARCH_HEIGHT.toFixed(2)} * exp(-ax * ax);

    float anchorY = clamp(
      ${ANCHOR_Y_BASE.toFixed(2)} + field * uAmplitude * ${HEIGHT_AMPLITUDE.toFixed(2)} + arch + rings * ${RING_LIFT.toFixed(2)},
      ${ANCHOR_Y_MIN.toFixed(2)}, ${ANCHOR_Y_MAX.toFixed(2)});

    float len = ${TUBE_LENGTH.toFixed(2)} * stretch;
    // uv.y 0 = bottom of the tube, 1 = the anchor it hangs from.
    float localY = (anchorY - len) + uv.y * len;

    // Cylindrical billboard around Y: yaw toward the camera, stay vertical.
    // cameraPosition is a ShaderMaterial built-in, so following the shared
    // Ken Burns drift costs no uniform write of our own.
    vec3 toCam = vec3(cameraPosition.x - g.x, 0.0, cameraPosition.z - g.y);
    float l = length(toCam);
    vec3 fwd = l > 0.0001 ? toCam / l : vec3(0.0, 0.0, 1.0);
    vec3 right = normalize(cross(vec3(0.0, 1.0, 0.0), fwd));

    vec3 worldPos = vec3(g.x, localY * uMirror, g.y)
                  + right * (position.x * ${TUBE_TOTAL_WIDTH.toFixed(3)});

    vec4 mvPosition = modelViewMatrix * vec4(worldPos, 1.0);
    gl_Position = projectionMatrix * mvPosition;

    vU = uv.x * 2.0 - 1.0;
    vV = uv.y;
    // Explicit atmospheric falloff on view distance.
    vFog = exp(-length(mvPosition.xyz) * ${ATMOSPHERIC_DENSITY.toFixed(3)});

    // Front-to-back colour gradient across the depth rows, each row drifting
    // slowly (~20s) with its own offset so the matrix is never one flat sheet.
    float mixT = clamp(iTube.w + sin(uTime * 0.314 + iTube.w * 2.4) * 0.25, 0.0, 1.0);
    vColor = mix(uColorA, uColorB, mixT);
    // Shockwaves brighten as well as lift - that is what makes a wave read
    // as struck rather than merely moved.
    vGlow = uPulse * (1.0 + rings * 1.35);

    // Mirrored copies fade with height above the floor.
    if (uMirror < 0.0) vGlow *= exp(-anchorY * ${REFLECTION_FALLOFF.toFixed(3)});

    vSafeAtt = safeZoneAttenuation(gl_Position);
  }
`;

const fragmentShader = `
  precision mediump float;

  uniform float uOpacity;
  uniform float uDesat;

  varying vec3 vColor;
  varying float vGlow;
  varying float vU;
  varying float vV;
  varying float vFog;
  varying float vSafeAtt;

  void main() {
    float au = abs(vU);
    // Hot core over the central 0.10 of the 0.30-wide quad, colour halo
    // falling off to the edges. No discard: on tile-based GPUs a discard in
    // a shader with this much coverage kills early-Z for the whole tile, so
    // the tube shape lives entirely in the alpha ramp.
    float core = 1.0 - smoothstep(0.0, ${TUBE_CORE_FRACTION.toFixed(4)}, au);
    float halo = 1.0 - au;
    halo *= halo;

    // Only 0.35 toward white: the album's colour has to stay franchement
    // recognizable, which the previous 0.75 destroyed.
    vec3 col = mix(vColor, vec3(1.0), core * 0.35) * vGlow;
    col = mix(vec3(dot(col, vec3(0.299, 0.587, 0.114))), col, 1.0 - uDesat);
    // Peak luminance under 1.0 so bloom (threshold 0.78) catches the cores
    // without blowing to flat white under ACES.
    col = min(col, vec3(0.92));

    float ends = smoothstep(0.0, 0.06, vV) * (1.0 - smoothstep(0.94, 1.0, vV));
    float alpha = (halo * 0.75 + core * 0.6) * ends * uOpacity * vFog * vSafeAtt;

    gl_FragColor = vec4(col, alpha);
  }
`;

export class KineticRigVisualizer extends Visualizer {
  // options.qualityTier (0/1/2, default 1) - see TIER_LAYOUT/DEFAULT_TIER.
  //
  // NOTE ON SIGNATURE: the brief specified `constructor(scene, camera,
  // options)`, but SceneManager (and every other visualizer) uses
  // `init(scene, camera)` with a no-arg-or-options constructor. Those two
  // requirements conflict; the codebase's actual contract wins, otherwise
  // SceneManager could not drive this class at all.
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;

    camera.position.set(CAMERA_POS[0], CAMERA_POS[1], CAMERA_POS[2]);
    camera.lookAt(CAMERA_LOOK[0], CAMERA_LOOK[1], CAMERA_LOOK[2]);

    this._rig = stageRig.acquire(RIG_OWNER, {
      scene: scene,
      intensityScale: RIG_INTENSITY_SCALE,
      zones: RIG_ZONES,
      floorLightBoost: RIG_FLOOR_LIGHT_BOOST
    });

    // A real lit floor, not a black void: MeshStandardMaterial so the rig's
    // SpotLights actually draw pools of light on it, which is what fills the
    // bottom third of the frame.
    const floorGeometry = new THREE.PlaneGeometry(FLOOR_SIZE, FLOOR_SIZE);
    const floorMaterial = new THREE.MeshStandardMaterial({
      color: FLOOR_COLOR,
      roughness: 0.78,
      metalness: 0.0
    });
    this._floor = new THREE.Mesh(floorGeometry, floorMaterial);
    this._floor.rotation.x = -Math.PI / 2;
    this._floor.receiveShadow = false;
    scene.add(this._floor);

    this._buildTubes(TIER_LAYOUT[this._tier]);

    this._time = 0;
    this._lastBeatPhase = null;
    // Phase 5 validation - see director.bandPeak (track-analysis.js's
    // per-band envelopes). null when no analysis timeline is available.
    this._lastBandPeakLow = null;
    this._beatCount = 0;
    this._ringCursor = 0;
    this._perimeterAngle = 0;
    this._lastPaletteRef = null;
    this._lastAspect = null;
    this._colorFrom = [new THREE.Color(FALLBACK_COLOR_A), new THREE.Color(FALLBACK_COLOR_B)];
    this._colorTo = [new THREE.Color(FALLBACK_COLOR_A), new THREE.Color(FALLBACK_COLOR_B)];
    this._colorLerp = 1;
    this._uniforms.uColorA.value.copy(this._colorTo[0]);
    this._uniforms.uColorB.value.copy(this._colorTo[1]);
    this._updateSpanX();
  }

  // Distance from the camera to the field's median row - the depth the
  // column span is sized against.
  _midRowDistance() {
    return CAMERA_POS[2] - this._midRowZ;
  }

  // The middle row must overflow the frame by 40% each side. Recomputed
  // whenever the aspect changes (see update) and pushed as a uniform, so a
  // resize never rebuilds a vertex buffer.
  _updateSpanX() {
    const vFovRad = THREE.MathUtils.degToRad(this._camera.fov);
    const visibleHeight = 2 * this._midRowDistance() * Math.tan(vFovRad / 2);
    const visibleWidth = visibleHeight * this._camera.aspect;
    this._uniforms.uSpanX.value = visibleWidth * SPAN_X_OVERFLOW;
    this._lastAspect = this._camera.aspect;
  }

  _buildTubes(layout) {
    const cols = layout.cols;
    const rows = layout.rows;
    const count = cols * rows;
    this._tubeCount = count;

    // (colNormalised, worldZ, phase, rowNormalised) - one vec4 instead of
    // four separate attributes.
    const tubes = new Float32Array(count * 4);

    const rowZ = [];
    for (let row = 0; row < rows; row++) {
      const f = rows > 1 ? row / (rows - 1) : 0;
      rowZ.push(ROW_Z_NEAR - ROW_Z_SPAN * Math.pow(f, ROW_Z_POWER));
    }
    // Median row depth, for the span calculation above.
    this._midRowZ = rowZ[Math.floor((rows - 1) / 2)];
    this._nearRowZ = rowZ[0];
    this._farRowZ = rowZ[rows - 1];

    let i = 0;
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        tubes[i * 4] = cols > 1 ? col / (cols - 1) - 0.5 : 0;
        tubes[i * 4 + 1] = rowZ[row];
        tubes[i * 4 + 2] = Math.random() * Math.PI * 2;
        tubes[i * 4 + 3] = rows > 1 ? row / (rows - 1) : 0;
        i++;
      }
    }

    // Depth extent the shockwaves travel across, in world Z - the field
    // always spans exactly ROW_Z_SPAN from the near row to the far one.
    this._ringRadiusRowSpan = ROW_Z_SPAN;

    const positions = new Float32Array([-0.5, 0, 0, 0.5, 0, 0, 0.5, 1, 0, -0.5, 1, 0]);
    const uvs = new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]);

    const geometry = new THREE.InstancedBufferGeometry();
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    geometry.setAttribute('iTube', new THREE.InstancedBufferAttribute(tubes, 4));
    geometry.instanceCount = count;
    // Placement is entirely shader-side, so the mesh's own bounds mean
    // nothing to the frustum culler.
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 4, -8), 80);
    this._geometry = geometry;

    const rings = [];
    for (let r = 0; r < RING_SLOTS; r++) rings.push(new THREE.Vector4(0, 0, 0, 0));

    // One set of holder objects shared by both materials, so the reflection
    // tracks the main pass with no extra per-frame writes. safeZoneUniforms
    // are shared app-wide and written once per resize by scene-manager.
    this._uniforms = Object.assign({}, safeZoneUniforms, {
      uTime: { value: 0 },
      uAmplitude: { value: 1 },
      uEnergy: { value: 0 },
      uRingSpeed: { value: 0 },
      uSpanX: { value: 30 },
      uRings: { value: rings },
      uColorA: { value: new THREE.Color(FALLBACK_COLOR_A) },
      uColorB: { value: new THREE.Color(FALLBACK_COLOR_B) },
      uPulse: { value: 1 }
    });

    const defines = layout.radial ? { USE_RADIAL: '' } : {};
    const common = {
      defines: defines,
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending
    };

    this._material = new THREE.ShaderMaterial(Object.assign({}, common, {
      uniforms: Object.assign({}, this._uniforms, {
        uMirror: { value: 1 },
        uOpacity: { value: 1 },
        uDesat: { value: 0 }
      })
    }));
    this._tubes = new THREE.Mesh(geometry, this._material);
    this._scene.add(this._tubes);

    // Tier 2 only: the same instanced geometry mirrored under the floor,
    // reading the very same animated uniforms. uMirror flips Y in-shader
    // rather than via a negative object scale, which would also flip the
    // billboard basis and the winding.
    this._reflectionMaterial = null;
    this._reflection = null;
    if (layout.reflection) {
      this._reflectionMaterial = new THREE.ShaderMaterial(Object.assign({}, common, {
        uniforms: Object.assign({}, this._uniforms, {
          uMirror: { value: -1 },
          uOpacity: { value: REFLECTION_OPACITY },
          uDesat: { value: REFLECTION_DESAT }
        }),
        // The mirrored copy lives BELOW the floor plane, so an opaque floor
        // depth-rejects every one of its pixels and the reflection never
        // appears at all. Drawing it without a depth test, ordered after the
        // floor, additively lays it over the floor instead - which is also
        // physically what a glossy floor reflection looks like.
        depthTest: false
      }));
      this._reflection = new THREE.Mesh(geometry, this._reflectionMaterial);
      this._reflection.renderOrder = 1;
      this._scene.add(this._reflection);
    }
  }

  _applyPalette(profile) {
    const vivid = profile.artworkPalette && profile.artworkPalette.vividColors;
    const hasVivid = vivid && vivid.length > 0;
    const a = hasVivid ? vivid[0] : FALLBACK_COLOR_A;
    const b = hasVivid ? vivid[Math.min(2, vivid.length - 1)] : FALLBACK_COLOR_B;
    this._colorFrom[0].copy(this._uniforms.uColorA.value);
    this._colorFrom[1].copy(this._uniforms.uColorB.value);
    this._colorTo[0].set(a);
    this._colorTo[1].set(b);
    this._colorLerp = 0;
  }

  // Rising edge of beatPhase (it ramps 0->1 then wraps). Every 4th beat is
  // the downbeat and fires a stronger wave from the centre; the others fire
  // from a slowly rotating point out on the perimeter. (The brief mentions a
  // Director-provided barPhase; no such field exists on the profile in this
  // project, so the local count is the implementation, not a fallback.)
  _emitRing() {
    const isDownbeat = this._beatCount % BEATS_PER_BAR === 0;
    let ox = 0;
    let oz = this._midRowZ;
    if (!isDownbeat) {
      this._perimeterAngle += 2.39996; // golden angle - successive off-beats land well apart
      const rx = this._uniforms.uSpanX.value * 0.35;
      const rz = this._ringRadiusRowSpan * 0.35;
      ox = Math.cos(this._perimeterAngle) * rx;
      oz = this._midRowZ + Math.sin(this._perimeterAngle) * rz;
    }
    const slot = this._uniforms.uRings.value[this._ringCursor];
    slot.set(ox, oz, this._time, isDownbeat ? RING_STRENGTH_DOWNBEAT : RING_STRENGTH_OFFBEAT);
    this._ringCursor = (this._ringCursor + 1) % RING_SLOTS;
    this._beatCount++;
  }

  update(deltaTime, profile) {
    stageRig.update(deltaTime, profile, RIG_OWNER);

    if (this._camera.aspect !== this._lastAspect) this._updateSpanX();

    const paused = profile.playbackState !== 'PLAYING';
    const bpm = profile.trackFeatures && profile.trackFeatures.bpm;
    const tempoMul = bpm
      ? THREE.MathUtils.clamp(bpm / REFERENCE_BPM, TEMPO_MULTIPLIER_MIN, TEMPO_MULTIPLIER_MAX)
      : NO_TEMPO_RATE;

    // Freezing _time freezes the height field, the stretch, and crucially
    // the age of any wave mid-flight, since ages are (uTime - startTime) in
    // this same clock. A wave paused halfway across stays halfway across.
    if (!paused) this._time += deltaTime * tempoMul;
    this._uniforms.uTime.value = this._time;

    // One wave crosses the field in exactly one beat period. _time runs at
    // tempoMul x wall-clock, so the beat period is expressed in those same
    // scaled units for the two to line up on screen.
    const maxRingRadius = Math.sqrt(
      Math.pow(this._uniforms.uSpanX.value * 0.5, 2) + Math.pow(this._ringRadiusRowSpan * 0.5, 2)
    );
    this._uniforms.uRingSpeed.value = bpm ? maxRingRadius / (tempoMul * (60 / bpm)) : 0;

    // Phase 5 validation - when a real analysis timeline is available, the
    // shock wave tracks the actual kick (director.bandPeak.low's own
    // rising edge, same exact-equality pattern laser-cathedral/
    // kaleido-drift/type-slam already use for accentPhrase) instead of a
    // plain per-beat metronome tick. Falls back to the original beatPhase-
    // wrap behavior unchanged whenever there's no analysis.
    const hasAnalysisBands = !!profile.analysisTimeline;
    if (hasAnalysisBands) {
      const bandPeakLow = profile.director.bandPeak.low;
      if (!paused && bandPeakLow === 1 && this._lastBandPeakLow !== 1) this._emitRing();
      this._lastBandPeakLow = bandPeakLow;
      this._lastBeatPhase = null;
    } else if (!paused && profile.beatPhase != null) {
      if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase) this._emitRing();
      this._lastBeatPhase = profile.beatPhase;
      this._lastBandPeakLow = null;
    } else if (profile.beatPhase == null) {
      // No tempo: no waves, and slots are cleared so nothing stays frozen
      // mid-flight from a previous track that did have BPM.
      this._lastBeatPhase = null;
      this._lastBandPeakLow = null;
      const ringValues = this._uniforms.uRings.value;
      for (let r = 0; r < RING_SLOTS; r++) ringValues[r].w = 0;
    }

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile);
    }
    if (this._colorLerp < 1) {
      this._colorLerp = Math.min(1, this._colorLerp + deltaTime / PALETTE_LERP_S);
      this._uniforms.uColorA.value.copy(this._colorFrom[0]).lerp(this._colorTo[0], this._colorLerp);
      this._uniforms.uColorB.value.copy(this._colorFrom[1]).lerp(this._colorTo[1], this._colorLerp);
    }

    const energy = profile.trackFeatures && profile.trackFeatures.energy;
    this._uniforms.uEnergy.value =
      energy != null && energy / 100 > ENERGY_THRESHOLD
        ? (energy / 100 - ENERGY_THRESHOLD) / (1 - ENERGY_THRESHOLD)
        : 0;

    const intensity = profile.director.intensity;
    this._uniforms.uAmplitude.value = 0.35 + intensity * 0.65;
    this._uniforms.uPulse.value = profile.director.pulse(BEAT_PULSE_AMOUNT, BEAT_PULSE_DECAY);
  }

  getLightRig() {
    return stageRig.get();
  }

  dispose() {
    stageRig.release(RIG_OWNER);
    this._rig = null;
    this._scene.remove(this._floor);
    this._floor.geometry.dispose();
    this._floor.material.dispose();
    this._scene.remove(this._tubes);
    if (this._reflection) this._scene.remove(this._reflection);
    // One shared geometry across both meshes - disposed once.
    this._geometry.dispose();
    this._material.dispose();
    if (this._reflectionMaterial) this._reflectionMaterial.dispose();
  }
}
